import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { useSelector } from "react-redux";

import useEntity from "../../hooks/useEntity";
import useLocalization from "../../hooks/useLocalization";
import useData from "../../hooks/useData";
import useSKU from "../../hooks/useSKU";
import useBrowser from "../../hooks/useBrowser";

import Page from "./Page";
import Card from "../layout/Card";

import Projects from "../ui/Projects";
import Skus from "../ui/Skus";

import SkuModal from "../modals/SkuModal";
import ClientEditor from "../editors/ClientEditor";

import EditorToolbar from "../editors/EditorToolbar";
import Input from "../elements/Input";

const Client = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getTitle, getLabel } = useLocalization();
  const { getFromServer } = useData();
  const { getSkus, skus } = useSKU();
  const { windowHeight } = useBrowser();
  const currentUserRole = useSelector((state) => state.authentication.role);
  const isSysAdmin = currentUserRole === "sysadmin";

  const [clientId, setClientId] = useState(null);
  const [projects, setProjects] = useState([]);

  const [openSkuModal, setOpenSkuModal] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);
  const [filterInventoryOnly, setFilterInventoryOnly] = useState(false);

  const {
    setEntityName,
    getEntityData,
    entityData,
    setEntityData,
    saveEntityData,
    updateField,
    message,
  } = useEntity();

  useEffect(() => {
    setEntityName("Client");
    setClientId(location.state.clientId);
  }, []);

  useEffect(() => {
    if (!clientId) return;
    getData();
  }, [clientId]);

  const getData = async () => {
    await getEntityData(clientId);
    const results = await getFromServer({
      entityName: "Project",
      filter: {
        clientId: clientId,
      },
    });
    setProjects(results);

    await getSkus({ clientId });
  };

  const handleToolbarClick = async (name) => {
    switch (name) {
      case "list":
        navigate("/clients");
        break;

      case "import":
        navigate("/clientimports", {
          state: {
            entityName: "Client",
            entityId: clientId,
            name: entityData.name,
          },
        });
        break;

      case "export":
        navigate("/clientexports", {
          state: {
            entityName: "Client",
            entityId: clientId,
            name: entityData.name,
          },
        });
        break;

      case "importExport":
        navigate("/clientimportexport", {
          state: {
            entityName: "Client",
            entityId: clientId,
            name: entityData.name,
          },
        });
        break;

      case "documents":
        navigate("/documents", {
          state: {
            scope: "client",
            id: clientId,
            name: entityData.name,
            returnUrl: "/client",
            returnState: { clientId: clientId },
          },
        });
        break;

      case "orders":
        navigate("/clientorders", {
          state: {
            clientId,
            clientName: entityData?.name || null,
            returnPath: location.pathname || "/client",
            returnState: { clientId },
          },
        });
        break;

      case "clientRefresh":
        if (!isSysAdmin) return;
        navigate("/clientrefresh", {
          state: {
            clientId,
          },
        });
        break;

      case "portalModules":
        navigate("/client-portal-modules", {
          state: {
            clientId,
            clientName: entityData?.name || null,
          },
        });
        break;

      case "refresh":
        await getData();
        break;
    }
  };

  const handleSave = async () => {
    await saveEntityData();
  };

  const handleSelectSku = (sku) => {
    setSelectedSku(sku);
    setOpenSkuModal(true);
  };

  const handleCloseSkuModal = () => {
    setSelectedSku(null);
    setOpenSkuModal(false);
  };

  const handleSaveSku = async () => {
    setSelectedSku(null);
    setOpenSkuModal(false);
    await getData();
  };

  const handleSaveProject = () => {};

  if (!entityData) return null;

  return (
    <>
      <Page
        pageName="client"
        entityName={entityData.name}
        includeToolbar
        items={[
          { name: "list", iconName: "ListAlt" },
          { name: "importExport", iconName: "CompareArrows" },
          { name: "orders", iconName: "ReceiptLong" },
          {
            name: "clientRefresh",
            iconName: "CloudSync",
            hide: !isSysAdmin,
          },
          { name: "portalModules", iconName: "Widgets" },
          { name: "documents", iconName: "Folder" },
          { name: "refresh", iconName: "Refresh" },
        ]}
        onToolItemClick={handleToolbarClick}
      >
        <Box
          display="flex"
          gap={1}
        >
          <Card
            title={getTitle("clientInformation")}
            sx={{
              flex: 1,
              height: `${windowHeight - 150}px`,
              overflow: "auto",
            }}
          >
            <ClientEditor
              clientId={entityData.id}
              onSave={getData}
              sx={{ backgroundColor: "#fff", color: "#000" }}
            />
          </Card>

          <Card
            title={getTitle("projects")}
            sx={{
              flex: 1,
              height: `${windowHeight - 150}px`,
              overflow: "auto",
            }}
          >
            <Projects
              clientId={entityData.id}
              data={projects}
              onSave={getData}
            />
          </Card>

          <Card
            title={`${getTitle("skus")} (${Array.isArray(skus) ? skus.length : 0})`}
            sx={{
              flex: 1,
              height: `${windowHeight - 150}px`,
              overflow: "auto",
            }}
          >
            <Box sx={{ px: 1, pt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={filterInventoryOnly}
                    onChange={(e) => setFilterInventoryOnly(e.target.checked)}
                  />
                }
                label="Inventory Items only"
              />
            </Box>
            <Skus
              clientId={entityData.id}
              data={
                filterInventoryOnly
                  ? (skus || []).filter((s) => s.isInventoryItem === true)
                  : skus
              }
              onSave={getData}
              onSelect={handleSelectSku}
            />
          </Card>
        </Box>
      </Page>

      {openSkuModal && (
        <SkuModal
          open={openSkuModal}
          onClose={handleCloseSkuModal}
          sku={selectedSku}
          clientId={entityData.id}
          onSave={handleSaveSku}
        />
      )}
    </>
  );
};

export default Client;
